/**
 * Burn-rate transforms for View C (RAISE-16867 D2/D8).
 *
 * All functions are pure — no side effects, no React.
 * Cost estimation uses window-level blended rates from the model breakdown,
 * avoiding the DEFAULT_PRICING Opus overestimate.
 */

import type { TokenCostItem } from "@/hooks/useTokenCost";

// Pricing table (per million tokens) — mirrors components/tokens/pricing.ts.
// Maintained in sync; do NOT import from there to keep this module dependency-free.
const PRICING_TABLE: Record<
  string,
  { input: number; output: number; cacheRead: number; cacheWrite: number }
> = {
  "claude-opus-4": { input: 15, output: 75, cacheRead: 1.875, cacheWrite: 18.75 },
  "claude-opus-4-6": { input: 15, output: 75, cacheRead: 1.875, cacheWrite: 18.75 },
  "claude-opus-4-8": { input: 15, output: 75, cacheRead: 1.875, cacheWrite: 18.75 },
  "claude-sonnet-4": { input: 3, output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  "claude-sonnet-4-5": { input: 3, output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  "claude-sonnet-4-6": { input: 3, output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  "claude-haiku-4": { input: 0.8, output: 4, cacheRead: 0.08, cacheWrite: 1.0 },
  "claude-haiku-4-5": { input: 0.8, output: 4, cacheRead: 0.08, cacheWrite: 1.0 },
};

const DEFAULT_PRICING = {
  input: 15,
  output: 75,
  cacheRead: 1.875,
  cacheWrite: 18.75,
};

function resolvePricing(model: string) {
  const normalized = model.replace(/"/g, "");
  for (const [key, val] of Object.entries(PRICING_TABLE)) {
    if (normalized.startsWith(key)) return val;
  }
  return DEFAULT_PRICING;
}

export interface ClassRates {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

export interface DailyCost {
  date: string;
  costUsd: number;
}

/**
 * Compute window-level blended rates from model-grouped token-cost items.
 *
 * For each token class, the blended rate = Σ(tokens_class,model × price_class,model)
 * / Σ(tokens_class,model). This corrects for the Sonnet-dominant actual mix
 * versus the Opus default.
 *
 * Returns null for empty input (no data to blend from).
 */
export function blendedRates(modelItems: TokenCostItem[]): ClassRates | null {
  if (modelItems.length === 0) return null;

  let wInput = 0;
  let wOutput = 0;
  let wCacheRead = 0;
  let wCacheWrite = 0;
  let totalInput = 0;
  let totalOutput = 0;
  let totalCacheRead = 0;
  let totalCacheWrite = 0;

  for (const item of modelItems) {
    const model = item.model ?? "unknown";
    const p = resolvePricing(model);
    wInput += item.input_tokens * p.input;
    wOutput += item.output_tokens * p.output;
    wCacheRead += item.cache_read * p.cacheRead;
    wCacheWrite += item.cache_write * p.cacheWrite;
    totalInput += item.input_tokens;
    totalOutput += item.output_tokens;
    totalCacheRead += item.cache_read;
    totalCacheWrite += item.cache_write;
  }

  // Avoid division by zero: fall back to default pricing for any zero-total class.
  return {
    input: totalInput > 0 ? wInput / totalInput : DEFAULT_PRICING.input,
    output: totalOutput > 0 ? wOutput / totalOutput : DEFAULT_PRICING.output,
    cacheRead:
      totalCacheRead > 0 ? wCacheRead / totalCacheRead : DEFAULT_PRICING.cacheRead,
    cacheWrite:
      totalCacheWrite > 0
        ? wCacheWrite / totalCacheWrite
        : DEFAULT_PRICING.cacheWrite,
  };
}

/**
 * Convert date-grouped token-cost items to daily cost estimates using the
 * provided blended rates.
 *
 * Items without a date are skipped (they are not day-level aggregations).
 */
export function toDailyCosts(
  dateItems: TokenCostItem[],
  rates: ClassRates,
): DailyCost[] {
  const M = 1_000_000;
  const results: DailyCost[] = [];
  for (const item of dateItems) {
    if (!item.date) continue;
    const costUsd =
      (item.input_tokens / M) * rates.input +
      (item.output_tokens / M) * rates.output +
      (item.cache_read / M) * rates.cacheRead +
      (item.cache_write / M) * rates.cacheWrite;
    results.push({ date: item.date, costUsd });
  }
  return results;
}

/**
 * Roll up daily costs into calendar-month buckets (YYYY-MM).
 */
export function toMonthlyCosts(
  days: DailyCost[],
): Array<{ month: string; costUsd: number }> {
  const buckets = new Map<string, number>();
  for (const d of days) {
    const month = d.date.slice(0, 7); // "YYYY-MM"
    buckets.set(month, (buckets.get(month) ?? 0) + d.costUsd);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, costUsd]) => ({ month, costUsd }));
}

/**
 * Compute MTD spend, trailing-7-day avg, and projected EOM.
 *
 * avgDaily7 = sum of last 7 *calendar* days across ALL input days (not
 * constrained to current month) / 7, so prior-month dates contribute
 * correctly in the first days of a new month.
 * projectedEom = mtd + avgDaily7 × remainingDays.
 *
 * Returns null when the current month has no data (nothing to project from).
 */
export function projectEndOfMonth(
  days: DailyCost[],
  todayIso: string,
): {
  mtd: number;
  avgDaily7: number;
  projectedEom: number;
  remainingDays: number;
} | null {
  const thisMonth = todayIso.slice(0, 7); // "YYYY-MM"

  // Filter to current month
  const monthDays = days.filter((d) => d.date.startsWith(thisMonth));
  if (monthDays.length === 0) return null;

  const mtd = monthDays.reduce((acc, d) => acc + d.costUsd, 0);

  // Build lookup from ALL days so trailing-7d avg includes prior-month dates
  const byDate = new Map<string, number>(days.map((d) => [d.date, d.costUsd]));

  // Trailing 7 calendar days (today inclusive, going back)
  const todayDate = new Date(todayIso + "T00:00:00Z");
  let sum7 = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(todayDate.getTime() - i * 86_400_000);
    const iso = d.toISOString().slice(0, 10);
    sum7 += byDate.get(iso) ?? 0;
  }
  const avgDaily7 = sum7 / 7;

  // Remaining days in month (days after today)
  const year = parseInt(todayIso.slice(0, 4), 10);
  const month = parseInt(todayIso.slice(5, 7), 10);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const todayDay = parseInt(todayIso.slice(8, 10), 10);
  const remainingDays = daysInMonth - todayDay;

  const projectedEom = mtd + avgDaily7 * remainingDays;

  return { mtd, avgDaily7, projectedEom, remainingDays };
}
